// Box-equivalent of the count-progress map. Same 13-tile
// geographic layout as the turnout tile grid (Prompt 15) so the
// reader who prefers abstracted tiles over geography can scan
// progress at a glance; the partisan-aware count-progress map
// remains available right above for the same data.
//
// Per-tile encoding:
//   • district number, prominent in the top-left
//   • a vertical amber fill that rises from the bottom of the tile
//     as count_progress_pct climbs from 0 → 100. 0% = empty, 100% =
//     fully amber. Matches the count-progress MAP's neutral amber
//     so the same district reads as the same colour intensity
//     across both surfaces.
//   • once the count is above 0, a small inline line showing the
//     running "PL n — PN n — N reads" split from the same editorial
//     vote fields the map already reads.
//
// Tap → an inline info panel below the grid (same pattern as the
// turnout tile grid + the count-progress map) with: count progress,
// last-update timestamp, PL / PN / OTHER %, total valid count, the
// editorial note if any, AND a "See lead on result map →"
// affordance that fires the same onSeeLead callback the
// count-progress map uses.

import { useMemo, useState } from 'react';
import { MAP_DATA } from '../../lib/buildMapPaths';
import { useElectionDayEditorial } from '../../lib/useElectionDayEditorial';
import type { DistrictEditorialResult } from '../../data/electionday-editorial-types';

// Geographic GRID_POSITION dropped in parallel with the turnout
// tile grid — both now flow numerically via the same responsive
// CSS grid (`repeat(auto-fit, minmax(120px, 1fr))`).

interface TileReading {
  districtId: number;
  countPct: number;
  plPct: number | null;
  pnPct: number | null;
  otherPct: number | null;
  totalValid: number | null;
  enteredAt: string | null;
}

export interface CountProgressTileGridProps {
  /** Wired through to the inline info panel's "see lead →" button.
   *  Same callback the page already passes to the count-progress
   *  map; firing it from here flashes the matching district on the
   *  result map exactly the same way. */
  onSeeLead?: (districtId: number) => void;
}

export default function CountProgressTileGrid({
  onSeeLead,
}: CountProgressTileGridProps) {
  const { data } = useElectionDayEditorial();
  const [selected, setSelected] = useState<number | null>(null);

  const tiles = useMemo<TileReading[]>(() => {
    const out = MAP_DATA.paths.map((p) =>
      computeReading(p.id, data.districts[`District ${p.id}`]),
    );
    out.sort((a, b) => a.districtId - b.districtId);
    return out;
  }, [data.districts]);

  const selectedTile = tiles.find((t) => t.districtId === selected) ?? null;
  const selectedEditorial = selectedTile
    ? data.districts[`District ${selectedTile.districtId}`]
    : undefined;

  return (
    <div className="electionday-progress-tiles">
      <header className="electionday-progress-tiles__head">
        <h3 className="electionday-progress-tiles__title">
          Count progress · district tiles
        </h3>
        <p className="electionday-progress-tiles__lede">
          Abstracted view of the same data as the count-progress map
          above. Fills rise from the bottom as a district's count
          closes in.
        </p>
      </header>

      <div
        className="electionday-progress-tiles__grid"
        role="list"
        aria-label="Count progress tiles"
      >
        {tiles.map((t) => {
          const isOpen = selected === t.districtId;
          return (
            <button
              key={t.districtId}
              type="button"
              role="listitem"
              className={`electionday-progress-tile${
                isOpen ? ' is-open' : ''
              }`}
              onClick={() =>
                setSelected((cur) =>
                  cur === t.districtId ? null : t.districtId,
                )
              }
              aria-pressed={isOpen}
            >
              <Tile reading={t} />
            </button>
          );
        })}
      </div>

      {selectedTile && (
        <InfoPanel
          tile={selectedTile}
          editorial={selectedEditorial}
          onSeeLead={onSeeLead}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

function computeReading(
  districtId: number,
  ed: DistrictEditorialResult | undefined,
): TileReading {
  if (!ed) {
    return {
      districtId,
      countPct: 0,
      plPct: null,
      pnPct: null,
      otherPct: null,
      totalValid: null,
      enteredAt: null,
    };
  }
  const valid = ed.total_valid;
  const plPct =
    valid && valid > 0 && ed.pl_votes !== null
      ? (ed.pl_votes / valid) * 100
      : null;
  const pnPct =
    valid && valid > 0 && ed.pn_votes !== null
      ? (ed.pn_votes / valid) * 100
      : null;
  const otherPct =
    valid && valid > 0 && ed.other_votes !== null
      ? (ed.other_votes / valid) * 100
      : null;
  return {
    districtId,
    countPct: ed.count_progress_pct ?? 0,
    plPct,
    pnPct,
    otherPct,
    totalValid: valid,
    enteredAt: ed.enteredAt,
  };
}

function Tile({ reading }: { reading: TileReading }) {
  const pct = Math.max(0, Math.min(100, reading.countPct));
  const showSplit =
    pct > 0 && reading.plPct !== null && reading.pnPct !== null;
  return (
    <div className="electionday-progress-tile__inner">
      <svg
        className="electionday-progress-tile__svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        {/* Bottom-up amber fill. Solid #c98012 matching the count-
            progress map's saturated end of the alpha ramp; the
            height of the fill IS the encoding here, not its alpha. */}
        <rect
          x={0}
          y={100 - pct}
          width={100}
          height={pct}
          fill="#c98012"
        />
      </svg>
      <span className="electionday-progress-tile__number">
        {reading.districtId}
      </span>
      <span className="electionday-progress-tile__pct">
        {pct > 0 ? `${Math.round(pct)}%` : '—'}
      </span>
      {showSplit && (
        <span className="electionday-progress-tile__split">
          PL {Math.round(reading.plPct!)} — PN {Math.round(reading.pnPct!)}
          {' — '}
          {reading.totalValid !== null
            ? `${reading.totalValid.toLocaleString()} reads`
            : '— reads'}
        </span>
      )}
    </div>
  );
}

interface InfoPanelProps {
  tile: TileReading;
  editorial: DistrictEditorialResult | undefined;
  onSeeLead?: (districtId: number) => void;
  onClose: () => void;
}

function InfoPanel({
  tile,
  editorial,
  onSeeLead,
  onClose,
}: InfoPanelProps) {
  return (
    <aside
      className="electionday-progress-tiles__info"
      aria-live="polite"
    >
      <div className="electionday-progress-tiles__info-head">
        <strong>District {tile.districtId}</strong>
        <button
          type="button"
          className="electionday-progress-tiles__info-close"
          onClick={onClose}
          aria-label="Close info panel"
        >
          ×
        </button>
      </div>
      <dl className="electionday-progress-tiles__info-grid">
        <dt>Count progress</dt>
        <dd>
          {tile.countPct > 0 ? `${Math.round(tile.countPct)}%` : '—'}
        </dd>

        <dt>Last update</dt>
        <dd>{tile.enteredAt ? formatStamp(tile.enteredAt) : '—'}</dd>

        <dt>PL</dt>
        <dd>
          {tile.plPct !== null ? `${tile.plPct.toFixed(1)}%` : '—'}
        </dd>

        <dt>PN</dt>
        <dd>
          {tile.pnPct !== null ? `${tile.pnPct.toFixed(1)}%` : '—'}
        </dd>

        <dt>Other</dt>
        <dd>
          {tile.otherPct !== null
            ? `${tile.otherPct.toFixed(1)}%`
            : '—'}
        </dd>

        <dt>Total valid</dt>
        <dd>
          {tile.totalValid !== null
            ? tile.totalValid.toLocaleString()
            : '—'}
        </dd>

        {editorial?.note && (
          <>
            <dt>Editorial note</dt>
            <dd>{editorial.note}</dd>
          </>
        )}
      </dl>
      {onSeeLead && (
        <div className="electionday-progress-tiles__info-actions">
          <button
            type="button"
            className="electionday-progress-tiles__info-link"
            onClick={() => onSeeLead(tile.districtId)}
          >
            See lead on result map →
          </button>
        </div>
      )}
    </aside>
  );
}

function formatStamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
